import { useEffect, useMemo, useState } from "react";

import PageHeader from "../components/PageHeader.jsx";
import apiClient from "../services/api.js";

const buildFormState = () => ({ principal_amount: "", interest_rate: "5", tenure_months: "12", purpose: "" });
const formatMoney = (value) => new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(Number(value) || 0);

const badgeTone = (status = "pending") => {
  const value = status.toLowerCase();
  if (["approved", "active", "disbursed"].includes(value)) return "bg-emerald-50 text-emerald-700";
  if (["declined", "rejected", "overdue"].includes(value)) return "bg-rose-50 text-rose-700";
  return "bg-amber-50 text-amber-700";
};

export default function LoansPage() {
  const [loans, setLoans] = useState([]);
  const [form, setForm] = useState(() => buildFormState());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    apiClient.get("/loans/").then((response) => setLoans(response.data || [])).catch(() => setLoans([])).finally(() => setLoading(false));
  }, []);

  const summary = useMemo(() => ({
    total: loans.reduce((sum, loan) => sum + Number(loan.principal_amount || 0), 0),
    active: loans.filter((loan) => ["approved", "active", "disbursed"].includes((loan.status || "").toLowerCase())).length
  }), [loans]);

  const repaymentEstimate = useMemo(() => {
    const principal = Number(form.principal_amount || 0);
    const months = Math.max(Number(form.tenure_months || 1), 1);
    const total = principal + (principal * Number(form.interest_rate || 0)) / 100;
    return total / months;
  }, [form]);

  const handleChange = (event) => setForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setStatus("");
    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/loans/", {
        ...form,
        principal_amount: Number(form.principal_amount),
        interest_rate: Number(form.interest_rate),
        tenure_months: Number(form.tenure_months)
      });
      setLoans((prev) => [data, ...prev]);
      setForm(buildFormState());
      setShowForm(false);
      setStatus("Your loan request has been submitted for review.");
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to submit your loan request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5 fade-up md:space-y-7">
      <PageHeader title="Loans" description="Apply, track approvals, and stay ahead of repayments." action={<button type="button" onClick={() => setShowForm((value) => !value)} className="rounded-2xl bg-accent px-4 py-2.5 text-xs font-semibold text-white shadow-sm">{showForm ? "Close" : "+ Apply"}</button>} />

      <section className="grid grid-cols-2 gap-3 md:gap-4">
        <article className="rounded-3xl bg-accent p-4 text-white md:p-6"><p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/60">Requested value</p><p className="mt-2 text-xl font-bold md:text-3xl">{formatMoney(summary.total)}</p><p className="mt-2 text-xs text-white/60">Across {loans.length} application{loans.length === 1 ? "" : "s"}</p></article>
        <article className="card-surface rounded-3xl p-4 md:p-6"><p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Active loans</p><p className="mt-2 text-2xl font-bold text-ink md:text-3xl">{summary.active}</p><p className="mt-2 text-xs text-slate-500">Approved or disbursed</p></article>
      </section>

      {status && <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{status}</div>}

      {showForm && (
        <form onSubmit={handleSubmit} className="card-surface rounded-[28px] p-5 md:p-7">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">Loan application</p><h2 className="mt-1 text-2xl font-semibold">Tell us what you need</h2>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <label className="text-sm font-semibold text-ink">Amount requested<div className="relative mt-2"><span className="absolute left-4 top-3 text-sm text-slate-400">₦</span><input name="principal_amount" type="number" step="0.01" min="1" required placeholder="0.00" value={form.principal_amount} onChange={handleChange} className="input pl-8" /></div></label>
            <label className="text-sm font-semibold text-ink">Repayment period<select name="tenure_months" value={form.tenure_months} onChange={handleChange} className="input mt-2"><option value="3">3 months</option><option value="6">6 months</option><option value="12">12 months</option><option value="18">18 months</option><option value="24">24 months</option></select></label>
            <label className="text-sm font-semibold text-ink">Interest rate<input name="interest_rate" type="number" min="0" step="0.01" value={form.interest_rate} onChange={handleChange} className="input mt-2" /></label>
            <label className="text-sm font-semibold text-ink">Purpose<textarea name="purpose" value={form.purpose} onChange={handleChange} placeholder="What will the loan support?" rows="3" className="input mt-2 resize-none" required /></label>
          </div>
          {Number(form.principal_amount) > 0 && <div className="mt-5 flex items-center justify-between rounded-2xl bg-sand/70 px-4 py-3"><div><p className="text-xs text-slate-500">Estimated monthly repayment</p><p className="mt-0.5 font-bold text-ink">{formatMoney(repaymentEstimate)}</p></div><span className="rounded-full bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Estimate</span></div>}
          {error && <p className="mt-4 rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-600">{error}</p>}
          <button type="submit" disabled={submitting} className="mt-5 w-full rounded-2xl bg-brand px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-brand/20 disabled:opacity-60 md:w-auto">{submitting ? "Submitting request..." : "Submit loan request"}</button>
        </form>
      )}

      <section className="card-surface rounded-[28px] p-5 md:p-7">
        <div><p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Applications</p><h2 className="mt-1 text-2xl font-semibold">Loan activity</h2></div>
        <div className="mt-5 space-y-3">
          {loading && <p className="py-6 text-center text-sm text-slate-500">Loading loans...</p>}
          {!loading && loans.map((loan) => (
            <article key={loan.id} className="rounded-2xl border border-black/5 bg-white/70 p-4">
              <div className="flex items-start justify-between gap-3"><div><p className="text-lg font-bold text-ink">{formatMoney(loan.principal_amount)}</p><p className="mt-1 text-xs text-slate-500">{loan.tenure_months} months · {loan.interest_rate}% interest</p></div><span className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${badgeTone(loan.status)}`}>{loan.status || "pending"}</span></div>
              {loan.purpose && <p className="mt-3 border-t border-black/5 pt-3 text-sm text-slate-600">{loan.purpose}</p>}
            </article>
          ))}
          {!loading && !loans.length && <div className="rounded-2xl bg-sand/60 px-4 py-8 text-center"><p className="text-sm font-semibold text-ink">No loan applications</p><p className="mt-1 text-xs text-slate-500">When you apply for a loan, its progress will appear here.</p></div>}
        </div>
      </section>
    </div>
  );
}
